import React from "react";
import {
  Checkbox,
  FileUploaderButton,
  InlineLoading,
  Tile,
} from "@carbon/react";
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import i18next from "../../i18n";

import TextField from "../../components/carbon/Form/TextField";
import SelectField from "../../components/carbon/Form/SelectField";
import TextareaField from "../../components/carbon/Form/TextareaField";
import { CURRENCY_LIST, PAYMENT_METHOD_LIST } from "../../utils";
import { OfferConditionsForm } from "./offerConditionsSchema";

interface OfferGeneralConditionsProps {
  control: Control<OfferConditionsForm>;
  watch: UseFormWatch<OfferConditionsForm>;
  setValue: UseFormSetValue<OfferConditionsForm>;
  hasServiceMaterial: boolean;
  /** The buyer collects (SCRUM-70): ask for the pickup address instead of the logistics. */
  buyerPicksUp?: boolean;
  fileName: string;
  isUploading: boolean;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const todayISO = () => new Date().toISOString().split("T")[0];

const OfferGeneralConditions: React.FC<OfferGeneralConditionsProps> = ({
  control,
  watch,
  setValue,
  hasServiceMaterial,
  buyerPicksUp = false,
  fileName,
  isUploading,
  onFileUpload,
}) => {
  const deliveryToBuyer = watch("deliveryToBuyer");
  const deliveryCostIncluded = watch("deliveryCostIncluded");
  const chargesDeliveryCost = deliveryToBuyer && !deliveryCostIncluded;

  return (
    <Tile className="offer-conditions">
      <h3 className="offer-conditions__heading">
        {i18next.t("offer.offerConditions")}
      </h3>

      <div className="offer-conditions__row">
        <TextField
          name="offerValidity"
          control={control}
          type="date"
          min={todayISO()}
          label={i18next.t("offer.offerValidUntil")}
        />
        <SelectField
          name="paymentMethod"
          control={control}
          label={i18next.t("offer.paymentMethod")}
          placeholder={i18next.t("validations.selectPaymentMethod")}
          options={PAYMENT_METHOD_LIST.map((pay) => ({
            value: pay.name,
            label: pay.name,
          }))}
        />
        <TextField
          name="paymentTerm"
          control={control}
          type="number"
          min="0"
          placeholder="e.g., 30"
          label={i18next.t("offer.paymentTerm")}
        />
      </div>

      {!hasServiceMaterial && buyerPicksUp && (
        <TextField
          name="pickupAddress"
          control={control}
          label={i18next.t("offer.pickupAddress")}
          helperText={i18next.t("offer.pickupAddressHint")}
          required
        />
      )}

      {!hasServiceMaterial && !buyerPicksUp && (
        <div className="offer-conditions__delivery">
          <Controller
            name="deliveryToBuyer"
            control={control}
            render={({ field }) => (
              <Checkbox
                id="deliveryToBuyer"
                labelText={i18next.t("offer.deliveryToBuyer")}
                checked={!!field.value}
                onChange={(_e, { checked }) => {
                  field.onChange(checked);
                  // Not delivering means the buyer handles logistics: there is
                  // no cost to include in the price, nor one to charge.
                  if (!checked) {
                    setValue("deliveryCostIncluded", false);
                    setValue("additionalDeliveryCost", "");
                  }
                }}
              />
            )}
          />

          <Controller
            name="deliveryCostIncluded"
            control={control}
            render={({ field }) => (
              <Checkbox
                id="deliveryCostIncluded"
                labelText={i18next.t("offer.deliveryCostIncluded")}
                checked={!!field.value}
                disabled={!deliveryToBuyer}
                onChange={(_e, { checked }) => {
                  field.onChange(checked);
                  if (checked) setValue("additionalDeliveryCost", "");
                }}
              />
            )}
          />

          <div className="offer-conditions__delivery-cost">
            <TextField
              name="additionalDeliveryCost"
              control={control}
              type="number"
              min="0"
              placeholder="50"
              disabled={!chargesDeliveryCost}
              label={i18next.t("offer.additionalDeliveryCost")}
            />
            <SelectField
              name="deliveryCostCurrency"
              control={control}
              disabled={!chargesDeliveryCost}
              label={i18next.t("offer.deliveryCostCurrency")}
              placeholder={i18next.t("validations.selectCurrency")}
              options={CURRENCY_LIST.map((currency) => ({
                value: currency.name,
                label: currency.name,
              }))}
            />
          </div>
        </div>
      )}

      <div className="offer-conditions__row">
        <TextField
          name="leadTime"
          control={control}
          type="number"
          min="0"
          placeholder="e.g., 15"
          label={
            hasServiceMaterial
              ? i18next.t("award.executionTerm")
              : i18next.t("offer.leadTimeAfterConfirmation")
          }
        />
      </div>

      <TextareaField
        name="generalComments"
        control={control}
        rows={3}
        label={i18next.t("offer.additionalComments")}
        placeholder={i18next.t("offer.additionalComments")}
      />

      <div className="offer-conditions__file">
        <span className="offer-conditions__file-label">
          {i18next.t("offer.file")}
        </span>
        {isUploading ? (
          <InlineLoading description={i18next.t("newMaterialForm.upload")} />
        ) : (
          <FileUploaderButton
            labelText={i18next.t("newMaterialForm.upload")}
            buttonKind="tertiary"
            size="sm"
            accept={[".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png", ".txt"]}
            disableLabelChanges
            onChange={onFileUpload}
          />
        )}
        {fileName && (
          <span className="offer-conditions__file-name">{fileName}</span>
        )}
        <p className="offer-conditions__file-hint">
          PDF, DOC, DOCX, JPG, PNG, TXT - 10MB max
        </p>
      </div>
    </Tile>
  );
};

export default OfferGeneralConditions;
